import PropTypes from 'prop-types';
import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const ClockTimeInputDialog = ({ open, onClose, onConfirm, minMinutes, maxMinutes }) => {
  const [timeInput, setTimeInput] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    // Validate time format (MM:SS or HH:MM:SS)
    const timeRegex = /^(\d{1,2}):(\d{2})(:(\d{2}))?$/;
    const match = timeInput.trim().match(timeRegex);

    if (!match) {
      setError('Please enter time in format MM:SS or HH:MM:SS (e.g., 20:30 or 1:20:30)');
      return;
    }

    const hours = match[4] ? parseInt(match[1], 10) : 0;
    const minutes = match[4] ? parseInt(match[2], 10) : parseInt(match[1], 10);
    const seconds = match[4] ? parseInt(match[4], 10) : parseInt(match[2], 10);

    if (minutes > 59 && hours > 0) { // Standard check, though minutes can be > 59 if hours is 0 often used in football like "90:00"
      // Actually football uses "90:00" -> 90 minutes. 
      // My regex: (\d{1,2}):(\d{2}) -> 90:00 -> match[1]=90, match[2]=00. 
      // Logic: match[4] is undefined. minutes=90.
      // The check `minutes > 59 || seconds > 59` below was blocking "90:00".
      // I should relax it if hours is 0, or just rely on total calculation? 
      // Original code: if (minutes > 59 || seconds > 59)
      // This blocks "90:00" if interpreted as MM:SS (minutes=90).
      // Wait, `match[4]` is undefined for "90:00".
      // minutes = 90.
      // `minutes > 59` -> true -> Error.
      // So "90:00" fails in original code?
      // Yes, user probably types "90:00" and it fails. I should fix this too.
      // But let's stick to requested feature first.
    }

    if (seconds > 59) {
      setError('Seconds must be between 0-59');
      return;
    }

    // If HH:MM:SS, minutes limit 59. If MM:SS, minutes can be anything (e.g. 105:00)
    if (match[4] && minutes > 59) {
      setError('Minutes must be between 0-59 when using hours');
      return;
    }

    // Convert to total seconds
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;
    const totalMinutesVal = totalSeconds / 60;

    // Range Validation
    if (minMinutes !== undefined && minMinutes !== null) {
      if (totalMinutesVal < minMinutes) {
        setError(`Time must be at least ${minMinutes} minutes.`);
        return;
      }
    }

    if (maxMinutes !== undefined && maxMinutes !== null) {
      if (totalMinutesVal > maxMinutes) {
        setError(`Time must be at most ${maxMinutes} minutes.`);
        return;
      }
    }

    // Format as MM:SS for display (simplified)
    // If > 60m, keep as MM:SS e.g. 90:00
    const displayM = Math.floor(totalSeconds / 60);
    const displayS = totalSeconds % 60;
    const formattedTime = `${String(displayM).padStart(2, '0')}:${String(displayS).padStart(2, '0')}`;

    onConfirm(formattedTime, totalSeconds);
    setTimeInput('');
    setError('');
  };

  const handleClose = () => {
    setTimeInput('');
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1E1E1E',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: '#FFFFFF',
          fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
          fontSize: 20,
          fontWeight: 600,
          pb: 1,
        }}
      >
        Input Time
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
              fontSize: 14,
              mb: 2,
            }}
          >
            {minMinutes !== undefined || maxMinutes !== undefined ?
              `Range: ${minMinutes ?? 0}m - ${maxMinutes ?? 'unlimited'}m` :
              'Enter the time to start the clock from (e.g., 20:30)'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Time (MM:SS)"
            placeholder="20:30"
            value={timeInput}
            onChange={(e) => {
              setTimeInput(e.target.value);
              setError('');
            }}
            error={!!error}
            helperText={error || 'Format: MM:SS (e.g., 20:30)'}
            sx={{
              '& .MuiInputBase-input': { color: '#FFFFFF' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#45FFD5' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                '&.Mui-focused fieldset': { borderColor: '#45FFD5' },
              },
              '& .MuiFormHelperText-root': { color: error ? '#D1030D' : 'rgba(255, 255, 255, 0.6)' },
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirm();
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ color: '#FFFFFF', textTransform: 'none' }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" sx={{ backgroundColor: '#45FFD5', color: '#000', fontWeight: 600, textTransform: 'none', '&:hover': { backgroundColor: '#3FE0C0' } }}>Overwrite Clock</Button>
      </DialogActions>
    </Dialog>
  );
};

ClockTimeInputDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  minMinutes: PropTypes.number,
  maxMinutes: PropTypes.number,
};

export default ClockTimeInputDialog;
